import { Box, Container, Flex } from '@moonpig/launchpad-components'
import { Breadcrumbs } from '@moonpig/web-core-nav'
import React, { FC } from 'react'
import { DepartmentsEnum } from '@moonpig/web-explore-types-graphql'
import { DispatchBanner } from '@moonpig/web-shared-products'
import type {
  AddedToBasketEvent,
  DispatchCutOffQuery,
  PageTypeEnum,
  ProductTileProduct,
} from '@moonpig/web-shared-products'
import { trackGAEvent } from '@moonpig/web-core-analytics'
import { styled } from '@moonpig/launchpad-utils'
import { system as s } from '@moonpig/launchpad-system'
import { getParentDepartment } from '@moonpig/web-core-utils'
import { useExperimentString } from '@moonpig/web-core-eppo/browser'
import { InternalUserGalleryInfo } from '../InternalUserGalleryInfo'
import { ProductGridWithTracking } from '../ProductGridWithTracking'
import { FixedBottomBar } from '../FixedBottomBar'
import { Breadcrumb } from '../../types'
import { generateBreadcrumbsClickGAEvent } from '../../analytics/GAEvents'
import { getSortByOptions } from '../../utils'
import { useFindLocaleText } from '../../text-localisation'
import { QuickFilters } from '../QuickFilters/QuickFilters'
import { FiltersMenu } from '../FiltersMenu'
import { FiltersCarousel } from '../FiltersCarousel'
import { InternalUserGalleryInfoVariant } from '../InternalUserGalleryInfoVariant'
import type { SearchContext } from '../../store/slices/shared'

const StyledBox = styled(Box)`
  ${s({
    bgcolor: 'colorBackground01',
    pb: 6,
  })}
`

const StyledFlex = styled(Flex)`
  display: flex;
  width: 100%;
`

const StyledHeader = styled(Box)`
  ${s({
    paddingTop: { xs: 6, md: 8 },
  })}
`

const StyledGridContainer = styled(Container)`
  position: relative;
  width: 100%;
`

export type SearchResultsProps = {
  renderHeader: () => React.ReactNode
  searchContext: SearchContext
  totalCount: number
  products: ProductTileProduct[]
  zeroResults: boolean
  experiments?: { [key: string]: string } | undefined
  suggestions: ProductTileProduct[]
  dispatchCutOffData?: DispatchCutOffQuery
  breadcrumbs: Breadcrumb[]
  isInternalUser: boolean
  addingToBasket: boolean
  onAddedToBasket: (e: AddedToBasketEvent) => Promise<void>
  searchTitle: string
  pageType: PageTypeEnum
  customerId?: string
  metaTitle: string
  groupCardProject?: string
  totalClickRankDocumentCount: number
}

export const SearchResults: FC<SearchResultsProps> = ({
  renderHeader,
  searchContext,
  totalCount,
  products,
  zeroResults,
  experiments,
  suggestions,
  dispatchCutOffData,
  breadcrumbs,
  isInternalUser,
  onAddedToBasket,
  searchTitle,
  pageType,
  customerId,
  metaTitle,
  groupCardProject,
  totalClickRankDocumentCount,
}) => {
  const localiseText = useFindLocaleText()
  const { searchTerm, promotionId, departments } = searchContext

  const useNewGalleryUX =
    useExperimentString('search-product-tile-ux', {
      fallback: 'disabled',
    })?.toLowerCase() === 'enabled'

  const useImprovedUnhappyFlows =
    useExperimentString('search-use-improved-unhappy-flows', {
      fallback: 'disabled',
    })?.toLowerCase() === 'enabled'

  const dispatchCutOff =
    dispatchCutOffData?.contentDepartments[0]?.dispatchCutOff

  const sortByOptions = getSortByOptions({
    isCards: getParentDepartment(departments[0]) === DepartmentsEnum.ALL_CARDS,
    localiseText,
  })

  const showFilterControls = !useImprovedUnhappyFlows || !zeroResults

  return (
    <StyledBox>
      {dispatchCutOff && <DispatchBanner dispatchCutOff={dispatchCutOff} />}
      <StyledHeader>
        {!!breadcrumbs.length && (
          <Box
            flex="0 0 auto"
            pt={8}
            onClick={() => {
              trackGAEvent(generateBreadcrumbsClickGAEvent(breadcrumbs))
            }}
            data-testid="breadcrumb-header"
          >
            <Breadcrumbs items={breadcrumbs} />
          </Box>
        )}
        {renderHeader()}
        {!useNewGalleryUX && isInternalUser && (
          <InternalUserGalleryInfo
            productCount={totalCount}
            searchTerm={searchTerm}
            filters={[]}
            promotionId={promotionId}
            departments={departments}
            totalClickRankDocumentCount={totalClickRankDocumentCount}
          />
        )}
        {
          /* istanbul ignore next */ useNewGalleryUX && isInternalUser && (
            <InternalUserGalleryInfoVariant
              productCount={totalCount}
              searchTerm={searchTerm}
              filters={[]}
              promotionId={promotionId}
              departments={departments}
              totalClickRankDocumentCount={totalClickRankDocumentCount}
            />
          )
        }
      </StyledHeader>

      <StyledFlex flexDirection="row">
        <FiltersMenu sortOptions={sortByOptions} />
        <StyledGridContainer padding={0}>
          {showFilterControls && (
            <>
              <QuickFilters departments={departments} />
              <FiltersCarousel />
            </>
          )}
          <ProductGridWithTracking
            totalCount={totalCount}
            pageTitle={searchTitle}
            metaTitle={metaTitle}
            searchContext={searchContext}
            products={
              (products?.length && products) ||
              (suggestions?.length && suggestions) ||
              []
            }
            onAddedToBasket={onAddedToBasket}
            isInternalUser={isInternalUser}
            presetFacets={[]}
            experiments={experiments}
            pageType={pageType}
            customerId={customerId}
            groupCardProject={groupCardProject}
          />
        </StyledGridContainer>
      </StyledFlex>

      <FixedBottomBar pageTitle={searchTitle} />
    </StyledBox>
  )
}
